/**
 * Simple rate limiter for form submissions using localStorage
 *
 * This provides basic protection against form spamming by tracking submissions in localStorage.
 * For production, a server-side rate limiting solution would be more secure.
 */

// Storage keys for different forms
export const RATE_LIMIT_KEYS = {
  CONTACT_FORM: 'contact_form_submissions',
  EMAIL_SUBSCRIPTION: 'email_subscription_submissions',
  CALL_REQUEST: 'call_request_submissions',
  LOGIN_ATTEMPT: 'login_attempts',
  SITE_AUDIT: 'site_audit_submissions',
};

// Maximum number of submissions in a time period
const DEFAULT_MAX_SUBMISSIONS = 5;

// Default time window (1 hour in milliseconds)
const DEFAULT_TIME_WINDOW_MS = 60 * 60 * 1000;

/**
 * Check if a user has exceeded their rate limit for a particular action
 *
 * @param storageKey The localStorage key to use for tracking this rate limit
 * @param maxSubmissions Maximum number of submissions allowed in the time window
 * @param timeWindowMs Time window in milliseconds
 * @returns boolean - true if the action should be allowed, false if rate limited
 */
export const checkRateLimit = (
  storageKey: string,
  maxSubmissions = DEFAULT_MAX_SUBMISSIONS,
  timeWindowMs = DEFAULT_TIME_WINDOW_MS
): boolean => {
  try {
    const now = Date.now();

    // Get existing submission records from localStorage
    const storedData = localStorage.getItem(storageKey);
    let submissions: { timestamp: number }[] = storedData
      ? JSON.parse(storedData)
      : [];

    // Filter out submissions outside the time window
    submissions = submissions.filter(sub => now - sub.timestamp < timeWindowMs);

    // Check if rate limit exceeded
    if (submissions.length >= maxSubmissions) {
      return false;
    }

    // Add current submission to the list
    submissions.push({ timestamp: now });
    localStorage.setItem(storageKey, JSON.stringify(submissions));

    return true;
  } catch (error) {
    console.error('Rate limit check error:', error);
    return true; // Allow submission if there's an error checking the rate limit
  }
};

/**
 * Get the remaining time until rate limit resets
 *
 * @param storageKey The localStorage key used for tracking this rate limit
 * @param timeWindowMs Time window in milliseconds
 * @returns Time in milliseconds until the oldest submission expires, or 0 if no waiting needed
 */
export const getTimeUntilRateLimitReset = (
  storageKey: string,
  timeWindowMs = DEFAULT_TIME_WINDOW_MS
): number => {
  try {
    const now = Date.now();
    const storedData = localStorage.getItem(storageKey);

    if (!storedData) return 0;

    const submissions: { timestamp: number }[] = JSON.parse(storedData);

    if (submissions.length === 0) return 0;

    // Sort submissions by timestamp (oldest first)
    submissions.sort((a, b) => a.timestamp - b.timestamp);

    // Calculate time until oldest submission expires
    const oldestSubmission = submissions[0];
    const expirationTime = oldestSubmission.timestamp + timeWindowMs;
    const timeUntilExpiration = expirationTime - now;

    return Math.max(0, timeUntilExpiration);
  } catch (error) {
    console.error('Error calculating rate limit reset time:', error);
    return 0;
  }
};

/**
 * Format the time until rate limit reset in a human-readable format
 *
 * @param timeMs Time in milliseconds
 * @returns Formatted time string (e.g., "2 minutes")
 */
export const formatTimeUntilReset = (timeMs: number): string => {
  const seconds = Math.ceil(timeMs / 1000);

  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }

  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};
